import { colors, screen, fonts } from 'components/styles/variables';
import css from 'styled-jsx/css';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useEvents from 'lib/hooks/useEvents';
import tapEvent from 'lib/Tracking/Vdp/SellerSnapshot/TapEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import { useContext } from 'react';
import { Address } from './types';
import Image from './Image';
import Caption from './Caption';

interface DirectionsButtonProps {
  href: string;
  className?: string;
}

function DirectionsButton({ href, className }: DirectionsButtonProps): JSX.Element {
  const { t } = useTranslation();
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);

  return (
    <>
      <a
        className={classNames('directions', className)}
        href={href}
        onClick={(): void => {
          logger.log(tapEvent({ action: 'get_directions', trackingParams }));
        }}
        target="_blank"
        aria-label={t('accessibility.newWindow')}
      >
        {t('vdp.directions')}
      </a>
      <style jsx>{`
        .directions {
          color: ${colors.WHITE};
          width: 100px;
          height: 36px;
          border-radius: 4px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-family: ${fonts.default};
          cursor: pointer;
          text-decoration: none;
          display: flex;
          align-items: center;
          letter-spacing: 0.2px;
          justify-content: center;
          color: ${colors.WHITE};
          background-color: ${colors.ALR1};
        }
        .directions:hover {
          background-color: ${colors.ALR2};
          color: ${colors.WHITE};
        }
        directions.disabled,
        [disabled] {
          pointer-events: none;
          opacity: ${0.4};
          color: ${colors.WHITE};
        }
      `}</style>
    </>
  );
}

const directionsButtonCss = css.resolve`
  display: none;

  @media screen and (min-width: ${screen.smMin}) {
    display: flex;
  }
`;

interface MapProps extends Address {
  mapUrl: string;
  imageUrl: string;
}

export default function Map({
  address,
  city,
  state,
  zip,
  mapUrl,
  imageUrl,
}: MapProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <a
        className="image-container"
        href={mapUrl}
        target="_blank"
        aria-label={t('accessibility.newWindow')}
      >
        <Image src={imageUrl} />
      </a>
      <Caption>
        <address className="address">
          {address}
          <br />
          {city}, {state} {zip}
        </address>
        <DirectionsButton className={classNames(directionsButtonCss.className)} href={mapUrl} />
        {directionsButtonCss.styles}
      </Caption>
      <style jsx>{`
        .image-container {
          line-height: 0;
        }

        .address {
          font-family: ${fonts.default};
          font-style: normal;
          font-weight: normal;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.2px;
          color: ${colors.BLACK};
        }

        @media screen and (min-width: ${screen.smMin}) {
          .address {
            font-size: 16px;
            line-height: 22px;
          }
        }
      `}</style>
    </>
  );
}
